import * as React from "react";
import {
    Card,
    Dialog,
    DialogContent,
    Button,
    Tooltip,
    DropdownTrigger,
    DropdownContent,
    Dropdown,
    DropdownItem,
    AlertDialog,
    AlertDialogContent,
    Badge,
    ActionButton,
    FilterButton,
} from "@jhool-io/fe-components";
import { useParams, useSearchParams } from "react-router-dom";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { isMinor } from "../../../../utils/helpers/age";
import EditBasicInfo from "./EditBasicInfo/EditBasicInfo";
import { useFetchClientBasicAndPersonalInfo } from "../../../../hooks/queries/client";
import ListState from "../../../ListState/ListState";
import Skeleton from "../../../Skeleton/Skeleton";
import { UserPermisions, UserRole } from "../../../../utils/types/user";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../../../hooks/permissions";
import {
    displayNameInRightFormat,
    formatDate,
    handleDisplayClientEmailCorrectly,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import { getStateInShortForm } from "../../../../utils/helpers/us-states/us-states";
import PinIcon from "../../../Icons/Pin";
import UnpinIcon from "../../../Icons/Unpin";
import { PinnedClientsContext } from "../../../../context/pinned-clients/PinnedClientsContext";
import { getProviderCredenetialPillStyle } from "../../../../modules/providers/helpers/providers.helper";
import {
    ClientStatus,
    FinancialAssistanceStatus,
} from "../../../../utils/types/client";
import { useFetchClientFlags } from "../../../../modules/clients/hooks/clients.queries";
import { useDeleteClientFlag } from "../../../../modules/clients/hooks/clients.mutations";
import useToast from "../../../../hooks/useToast";
import EditClientFlagForm from "../../../../modules/clients/components/EditClientFlagForm/EditClientFlagForm";
import AddClientFlagForm from "../../../../modules/clients/components/AddClientFlagForm/AddClientFlagForm";
import { useAppSelector } from "../../../../hooks/useRedux";
import {
    getClientFlagDetails,
    DEFAULT_FLAG_COLOR,
} from "../../../../modules/clients/helpers/clients.helpers";
import ClientTagBadges from "../../../../modules/clients/components/ClientTagBadges/ClientTagBadges";
import EditClientTagsMenu from "../../../../modules/clients/components/EditClientTagsMenu/EditClientTagsMenu";

import Loader from "../../../../shared-ui/Loader/Loader";
import ChevronUpIcon from "../../../../icons/ChevronUp";
import ChevronDownIcon from "../../../../icons/ChevronDown";
import EditIcon from "../../../../icons/Edit";
import {
    useCanSetClientBiller,
    useGetClientCanByChargedByText,
} from "../../../../modules/billing/hooks/helpers.hooks";
import { useGetClientSyncStatus } from "../../../../modules/e-prescirbe/hooks/e-prescribe.queries";
// import RefreshIcon from "../../../../icons/Refresh";
import useCanViewEPrescribeFlow from "../../../../modules/e-prescirbe/hooks/useCanViewEPrescribeFlow";

type Dialogs = "edit-info" | "add-flag" | "edit-flag" | "delete-flag";

export default function BasicInfo() {
    const isSmallScreen = window.matchMedia("(max-width: 768px)").matches;

    // Component local states
    const [isMaximised, setIsMaximised] = React.useState(() => !isSmallScreen);
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );

    const [selectedFlagId, setSelectedFlagId] = React.useState<string | null>(
        null
    );

    const itemsRef = React.useRef<HTMLDivElement>(null);

    // Get id from url params
    const params = useParams();
    const clientId = params.clientId as string;

    const handleToggleMaximizeState = () => {
        setIsMaximised((state) => !state);
    };

    // Fetch client basic info
    const { data, error } = useFetchClientBasicAndPersonalInfo(clientId);

    const { practice } = useAppSelector((state) => state.userPractice);
    const canViewEPrescribeFlow = useCanViewEPrescribeFlow();

    const userCanViewFlags = !(
        practice?.roles.length === 1 &&
        practice?.roles?.[0] === UserRole.PROVIDER
    );

    const userCanViewTags = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_TAGS_READ,
    ]);

    const userCanEditTags = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_TAGS_WRITE,
    ]);

    // Fetch client flags
    const clientFlags = useFetchClientFlags(clientId, userCanViewFlags);

    const deleteFlag = useDeleteClientFlag(clientId, selectedFlagId || "");

    const pinnedClientsContext = React.useContext(PinnedClientsContext);

    const {
        data: syncStatus,
        isLoading: syncStatusIsLoading,
        error: syncStatusError,
    } = useGetClientSyncStatus(practice?.practice_id as string, clientId);

    // Get serach params from url
    const [searchParams] = useSearchParams();
    const tabInView = searchParams.get("tab");

    // Function for opening or closing modal
    const handleDialogInView = (modal: Dialogs | null) => {
        setDialogInView(() => modal);
    };

    const { toast } = useToast();

    const queryClient = useQueryClient();

    const clientCanBeChargedBy = useGetClientCanByChargedByText();

    // Function to delete client flag
    const handleDeleteFlag = () => {
        deleteFlag.mutate(null, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [clientId, "client-flags"],
                });
                toast({
                    mode: "success",
                    message: res.message || "Flag added successfully",
                });
                setSelectedFlagId(null);
                setDialogInView(null);
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not add flag at this time.",
                });
            },
        });
    };

    // Roles restricted from editing
    const restrictedRoles = [UserRole.AUDITOR, UserRole.PROVIDER];

    // check if logged in user can edit client info
    const hasEditPermission = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.CLIENT_INFO_PERSONAL_WRITE,
        UserPermisions.CLIENT_INFO_ALL_WRITE,
        UserPermisions.CLIENT_INFO_PROVIDER_WRITE,
    ]);

    const hasRestrictedRole = useDoesUserHaveRole(restrictedRoles);
    const isSuperAdmin = useDoesUserHaveRole([UserRole.SUPER_ADMIN]);
    const isAuditorAdmin = useDoesUserHaveRole([UserRole.AUDITOR_ADMIN]);

    // Check if the logged in user can edit personal info
    const canEditClientInfo =
        hasEditPermission &&
        (!hasRestrictedRole || isSuperAdmin || isAuditorAdmin);

    const canSetClientBiller = useCanSetClientBiller();

    const canSeeProviderHistory = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.CLIENT_INFO_PROVIDER_READ,
        UserPermisions.CLIENT_INFO_PROVIDER_WRITE,
    ]);

    // Function for scrolling to provider history list section
    const handleProviderHistoryButtonClick = () => {
        /**  Cannot access element ref here because
         provider history list is not in this
         component so we perform action directly on the dom */
        const providerHistoryHtml = document.getElementById(
            "provider-history"
        ) as HTMLDivElement;

        if (providerHistoryHtml) {
            providerHistoryHtml.scrollIntoView({ behavior: "smooth" });
        }
    };

    // To determine if mutation is currently running, returns 0 || 1
    const isMutating = useIsMutating(["edit-personal-info", clientId]);
    const addFlagIsMutating = useIsMutating(["add-client-flag", clientId]);
    const editFlagIsMutating = useIsMutating([
        "update-client-flag",
        selectedFlagId,
    ]);

    const getConsentStatus = (consent: boolean | undefined) => {
        switch (consent) {
            case true:
                return "Yes";
            case false:
                return "No";
            default:
                return "--";
        }
    };

    const getPortalActivationStatus = (lastLoginDate?: string) => {
        if (lastLoginDate && !Number.isNaN(Date.parse(lastLoginDate))) {
            return "Yes";
        }
        return "No";
    };

    const getPortalActivationDate = (firstLoginDate?: string) => {
        if (firstLoginDate && !Number.isNaN(Date.parse(firstLoginDate))) {
            return formatDate(firstLoginDate, false);
        }
        return null;
    };

    const getLastLoginDate = (lastLoginDate?: string | null) => {
        if (lastLoginDate && !Number.isNaN(Date.parse(lastLoginDate))) {
            return formatDate(lastLoginDate, false);
        }
        return null;
    };

    const getClientSyncStatusForDisplay = () => {
        if (syncStatusIsLoading) return <Loader loaderType="inline" />;
        if (syncStatusError)
            return (
                <span className="text-danger !lowercase">
                    cannot fetch status
                </span>
            );
        return syncStatus.data.is_ready_for_prescribing ? "Yes" : "No";
    };

    React.useEffect(() => {
        if (itemsRef.current) {
            if (isMaximised) {
                itemsRef.current.style.height = `auto`;
            } else {
                itemsRef.current.style.height = `0`;
            }
        }
    }, [isMaximised, isSmallScreen]);

    const primaryAssignee = data?.data.primary_assignee;
    const secondaryAssignee = data?.data.secondary_assignee;
    const coordinatorDisplay =
        !primaryAssignee?.first_name && !secondaryAssignee?.first_name
            ? "--"
            : [
                  primaryAssignee?.first_name
                      ? displayNameInRightFormat({
                            firstName: primaryAssignee.first_name,
                            lastName: primaryAssignee.last_name,
                        })
                      : "",
                  secondaryAssignee?.first_name
                      ? displayNameInRightFormat({
                            firstName: secondaryAssignee.first_name,
                            lastName: secondaryAssignee.last_name,
                        })
                      : "",
              ]
                  .filter(Boolean)
                  .join(", ");

    return (
        <>
            <Dialog open={dialogInView === "edit-info" && Boolean(data)}>
                <DialogContent
                    title="Edit personal info"
                    handleCloseDialog={() => handleDialogInView(null)}
                    variant="center"
                    cancelText="Cancel"
                    saveText="Save Changes"
                    submitBtnFormValue="edit-basic-info"
                    isCancelBtnDisabled={isMutating > 0}
                    isSubmitBtnDisabled={isMutating > 0}
                >
                    <div>
                        {data?.data && (
                            <EditBasicInfo
                                clientBasicInfo={data.data}
                                onFormSubmit={() => handleDialogInView(null)}
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "add-flag" && Boolean(clientId)}>
                <DialogContent
                    title="Add client flag"
                    handleCloseDialog={() => handleDialogInView(null)}
                    variant="center"
                    saveText="Add flag"
                    submitBtnFormValue="add-client-flag"
                    isCancelBtnDisabled={addFlagIsMutating > 0}
                    isSubmitBtnDisabled={addFlagIsMutating > 0}
                >
                    <AddClientFlagForm
                        onFormSubmit={() => handleDialogInView(null)}
                    />
                </DialogContent>
            </Dialog>
            <AlertDialog
                open={dialogInView === "delete-flag" && Boolean(selectedFlagId)}
            >
                <AlertDialogContent
                    title="Are you sure you want to delete flag?"
                    description="This will delete this flag from the client flags and cannot be reversed"
                    saveText="Delete flag"
                    isCancelBtnDisabled={addFlagIsMutating > 0}
                    isSaveBtnDisabled={addFlagIsMutating > 0}
                    onCancelClick={() => handleDialogInView(null)}
                    onSaveClick={handleDeleteFlag}
                    isDeleting
                />
            </AlertDialog>
            <Dialog
                open={dialogInView === "edit-flag" && Boolean(selectedFlagId)}
            >
                <DialogContent
                    title="Edit client flag"
                    handleCloseDialog={() => handleDialogInView(null)}
                    variant="center"
                    saveText="Save changes"
                    submitBtnFormValue="edit-client-flag"
                    isCancelBtnDisabled={editFlagIsMutating > 0}
                    isSubmitBtnDisabled={editFlagIsMutating > 0}
                >
                    <EditClientFlagForm
                        onFormSubmit={() => handleDialogInView(null)}
                        flagId={selectedFlagId || ""}
                    />
                </DialogContent>
            </Dialog>
            {error && (
                <ListState
                    isError
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display client basic info at this time please try again later`
                    }
                    listHeader="BASIC INFO"
                    stateHelperText="Try reloading this page to solve this issue"
                />
            )}

            {!error && (
                <Card>
                    <div className="flex justify-between items-center gap-[--spacing-items] flex-wrap">
                        {(data && (
                            <h3 className="text-title flex items-center flex-wrap gap-x-2 gap-y-2">
                                <>
                                    <span className="capitalize">
                                        {displayNameInRightFormat({
                                            firstName: data.data.first_name,
                                            lastName: data.data.last_name,
                                        })}
                                    </span>
                                    {data.data.client_is_b2b ? (
                                        <span>
                                            <Badge
                                                content={
                                                    data.data.client_is_b2b
                                                        ? "B2B"
                                                        : ""
                                                }
                                                type="warning"
                                            />
                                        </span>
                                    ) : null}

                                    {isMinor(data.data.age) ? (
                                        <span>
                                            <Badge
                                                content=" Minor"
                                                type="danger"
                                            />
                                        </span>
                                    ) : null}
                                    {getLastLoginDate(
                                        data.data.last_login_date
                                    ) ? (
                                        <Tooltip
                                            content={
                                                data.data.last_login_date
                                                    ? `Last login: ${formatDate(
                                                          data.data
                                                              .last_login_date,
                                                          false
                                                      )}`
                                                    : "No login activity"
                                            }
                                        >
                                            <Button
                                                size="auto"
                                                variant="normal"
                                            >
                                                <Badge
                                                    content="Portal"
                                                    type="info"
                                                />
                                            </Button>
                                        </Tooltip>
                                    ) : null}
                                    {data.data.financial_assistance_status &&
                                    (data.data.financial_assistance_status ===
                                        FinancialAssistanceStatus.EXPIRED ||
                                        data.data
                                            .financial_assistance_status ===
                                            FinancialAssistanceStatus.YES) ? (
                                        <Tooltip
                                            content={
                                                data?.data
                                                    .financial_assistance_end_date
                                                    ? `${
                                                          data.data
                                                              .financial_assistance_start_date
                                                              ? `Starts ${formatDate(
                                                                    data.data
                                                                        .financial_assistance_start_date,
                                                                    true
                                                                )}, `
                                                              : ""
                                                      }Ends ${formatDate(
                                                          data.data
                                                              .financial_assistance_end_date,
                                                          true
                                                      )}`
                                                    : "Date not added"
                                            }
                                        >
                                            <Button
                                                variant="normal"
                                                size="auto"
                                            >
                                                <Badge
                                                    content={
                                                        data.data
                                                            .financial_assistance_status ===
                                                        FinancialAssistanceStatus.EXPIRED
                                                            ? "Expired Financial Assistance"
                                                            : "Financial assistance"
                                                    }
                                                    type={
                                                        data.data
                                                            .financial_assistance_status ===
                                                        FinancialAssistanceStatus.EXPIRED
                                                            ? "danger"
                                                            : "info"
                                                    }
                                                />
                                            </Button>
                                        </Tooltip>
                                    ) : null}
                                    {data?.data.client_status ===
                                    ClientStatus.INACTIVE ? (
                                        <Badge
                                            content="Inactive"
                                            type="danger"
                                            className="capitalize"
                                        />
                                    ) : (
                                        <span>
                                            <Badge
                                                content={
                                                    data?.data.client_status
                                                }
                                                type="success"
                                                className="capitalize"
                                            />
                                        </span>
                                    )}
                                </>
                            </h3>
                        )) || <Loader loaderType="inline" />}

                        {(data && (
                            <div className="flex gap-x-[--spacing-items] items-center">
                                {userCanViewFlags ? (
                                    <ActionButton
                                        variant="secondary"
                                        onClick={() =>
                                            handleDialogInView("add-flag")
                                        }
                                        actionTitle="Add client flag"
                                    >
                                        Add flag
                                    </ActionButton>
                                ) : null}
                                {canSeeProviderHistory &&
                                    (!tabInView ||
                                        tabInView === "personal-info") && (
                                        <ActionButton
                                            variant="secondary"
                                            onClick={
                                                handleProviderHistoryButtonClick
                                            }
                                            className="hidden md:flex"
                                            actionTitle="View sessions history"
                                        >
                                            Sessions history
                                        </ActionButton>
                                    )}

                                {canEditClientInfo && (
                                    <>
                                        <ActionButton
                                            onClick={() =>
                                                handleDialogInView("edit-info")
                                            }
                                            className="hidden md:flex"
                                            actionTitle="Edit client details"
                                        >
                                            Edit
                                        </ActionButton>
                                        <Button
                                            variant="can-hover"
                                            size="auto"
                                            className="rounded-full bg-[#576a810d] flex items-center justify-center md:hidden"
                                            onClick={() =>
                                                handleDialogInView("edit-info")
                                            }
                                        >
                                            <EditIcon />
                                        </Button>
                                    </>
                                )}
                                {pinnedClientsContext?.isClientPinnedInLocation(
                                    clientId,
                                    "client_chart"
                                ) ? (
                                    <ActionButton
                                        onClick={() => {
                                            pinnedClientsContext.handleUnpinClient(
                                                clientId,
                                                "client_chart"
                                            );
                                        }}
                                        actionTitle="Unpin client"
                                    >
                                        <UnpinIcon />
                                    </ActionButton>
                                ) : (
                                    <ActionButton
                                        onClick={() =>
                                            pinnedClientsContext?.handlePinClient(
                                                {
                                                    clientId,
                                                    clientName: `${data.data.first_name} ${data.data.last_name}`,
                                                    pinModule: "client_chart",
                                                }
                                            )
                                        }
                                        actionTitle="Pin client"
                                    >
                                        <PinIcon />
                                    </ActionButton>
                                )}
                                <ActionButton
                                    onClick={handleToggleMaximizeState}
                                    actionTitle={
                                        isMaximised
                                            ? "Minimize view"
                                            : "Maximize view"
                                    }
                                >
                                    {isMaximised ? (
                                        <ChevronUpIcon />
                                    ) : (
                                        <ChevronDownIcon />
                                    )}
                                </ActionButton>
                            </div>
                        )) || <Loader loaderType="inline" />}
                    </div>

                    {data ? (
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-caption text-textSec capitalize font-medium">
                                {data.data.pronouns || "--"}
                            </span>
                            {userCanViewTags ? (
                                <ClientTagBadges
                                    clientId={clientId}
                                    badgeClassName="!text-caption px-2 h-5"
                                />
                            ) : null}
                            {userCanEditTags ? (
                                <EditClientTagsMenu clientId={clientId}>
                                    <Button
                                        variant="normal"
                                        size="auto"
                                        className="text-primary !text-caption font-semibold h-5 flex items-center"
                                    >
                                        Edit profile tags
                                        <ChevronDownIcon
                                            className="ml-1"
                                            stroke="currentColor"
                                        />
                                    </Button>
                                </EditClientTagsMenu>
                            ) : null}
                        </div>
                    ) : null}

                    <div className="mt-[--spacing-items] flex gap-2 items-center flex-wrap">
                        {clientFlags.data &&
                            clientFlags.data.data.length > 0 &&
                            clientFlags.data.data.map((flag) => {
                                const parsedFlag = getClientFlagDetails(
                                    flag.content
                                );
                                return (
                                    <Dropdown key={flag.client_flag_id}>
                                        <DropdownTrigger asChild>
                                            <FilterButton
                                                className="capitalize !min-h-7"
                                                style={{
                                                    backgroundColor:
                                                        parsedFlag?.color ??
                                                        DEFAULT_FLAG_COLOR,
                                                    color: "#ffffff",
                                                    textTransform: "capitalize",
                                                }}
                                            >
                                                {parsedFlag?.label ?? ""}
                                            </FilterButton>
                                        </DropdownTrigger>
                                        <DropdownContent
                                            width="auto"
                                            align="end"
                                        >
                                            <DropdownItem
                                                onClick={() => {
                                                    setSelectedFlagId(
                                                        flag.client_flag_id
                                                    );
                                                    handleDialogInView(
                                                        "edit-flag"
                                                    );
                                                }}
                                            >
                                                Edit
                                            </DropdownItem>
                                            <DropdownItem
                                                onClick={() => {
                                                    setSelectedFlagId(
                                                        flag.client_flag_id
                                                    );
                                                    handleDialogInView(
                                                        "delete-flag"
                                                    );
                                                }}
                                            >
                                                Delete
                                            </DropdownItem>
                                        </DropdownContent>
                                    </Dropdown>
                                );
                            })}
                    </div>

                    <div
                        ref={itemsRef}
                        className="transition-all h-0 duration-1000 ease-out overflow-y-auto"
                    >
                        <div className="rounded-md bg-[--bg-sidebar-dropdown-open] px-[--padding-card-x] py-[--padding-card-y-lg] mt-[--spacing-header]">
                            {!data?.data ? (
                                <Loader />
                            ) : (
                                <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-y-[--spacing-items] gap-x-2 overflow-y-auto">
                                    <div className="text-caption font-semibold">
                                        <p className="text-textSec">
                                            CLIENT CHART NUMBER
                                        </p>
                                        {(data && (
                                            <p className="text-base font-medium capitalize">
                                                {data.data
                                                    .client_chart_number ||
                                                    "--"}
                                            </p>
                                        )) || (
                                            <Skeleton
                                                containerTestId="basicinfo-loader"
                                                width="150px"
                                            />
                                        )}
                                    </div>
                                    <div className=" text-caption font-semibold">
                                        <p className="text-textSec">
                                            PREFERRED NAME
                                        </p>
                                        {(data && (
                                            <p className="text-base font-medium capitalize">
                                                {data.data.preferred_name ||
                                                    "--"}
                                            </p>
                                        )) || (
                                            <Skeleton
                                                containerTestId="basicinfo-loader"
                                                width="150px"
                                            />
                                        )}
                                    </div>
                                    <div className=" text-caption font-semibold">
                                        <p className="text-textSec">SEX</p>
                                        {(data && (
                                            <p className="text-base font-medium capitalize">
                                                {data.data.sex || "--"}
                                            </p>
                                        )) || (
                                            <Skeleton
                                                containerTestId="basicinfo-loader"
                                                width="150px"
                                            />
                                        )}
                                    </div>
                                    <div className=" text-caption font-semibold">
                                        <p className="text-textSec">AGE</p>
                                        {(data && (
                                            <p className="text-base font-medium capitalize">
                                                {data.data.age} yrs
                                            </p>
                                        )) || (
                                            <Skeleton
                                                containerTestId="basicinfo-loader"
                                                width="150px"
                                            />
                                        )}
                                    </div>
                                    <div className=" text-caption font-semibold">
                                        <p className="text-textSec">
                                            DATE OF BIRTH
                                        </p>

                                        {(data && (
                                            <p className="text-base font-medium capitalize">
                                                {formatDate(
                                                    data.data.date_of_birth,
                                                    true
                                                )}
                                            </p>
                                        )) || (
                                            <Skeleton
                                                containerTestId="basicinfo-loader"
                                                width="150px"
                                            />
                                        )}
                                    </div>
                                    <div className=" text-caption font-semibold ">
                                        <p className="text-textSec"> CONTACT</p>

                                        {(data && (
                                            <p className="text-base font-medium capitalize">
                                                {data.data.contact}
                                            </p>
                                        )) || (
                                            <Skeleton
                                                containerTestId="basicinfo-loader"
                                                width="150px"
                                            />
                                        )}
                                    </div>
                                    <div className=" text-caption font-semibold  uppercase">
                                        <p className="text-textSec">EMAIL</p>
                                        {(data && (
                                            <p className="text-base font-medium lowercase">
                                                {handleDisplayClientEmailCorrectly(
                                                    data.data.email
                                                )}
                                            </p>
                                        )) || (
                                            <Skeleton
                                                containerTestId="basicinfo-loader"
                                                width="150px"
                                            />
                                        )}
                                    </div>

                                    <div className=" text-caption font-semibold  uppercase">
                                        <p className="text-textSec">PROVIDER</p>
                                        {(data && (
                                            <div className="flex flex-col gap-y-1">
                                                {data.data.current_providers
                                                    ?.length ? (
                                                    data.data.current_providers.map(
                                                        (provider) => (
                                                            <div
                                                                key={
                                                                    provider.provider_id
                                                                }
                                                                className="flex min-w-0 items-start gap-x-2"
                                                            >
                                                                <p className="min-w-0 text-base font-medium capitalize leading-tight">
                                                                    {displayNameInRightFormat(
                                                                        provider.provider_name
                                                                    )}
                                                                </p>
                                                                {provider.credential && (
                                                                    <span className="shrink-0">
                                                                        {getProviderCredenetialPillStyle(
                                                                            provider.credential
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )
                                                    )
                                                ) : (
                                                    <p className="text-base font-medium">
                                                        --
                                                    </p>
                                                )}
                                            </div>
                                        )) || (
                                            <Skeleton
                                                containerTestId="basicinfo-loader"
                                                width="150px"
                                            />
                                        )}
                                    </div>

                                    <div className=" text-caption font-semibold  uppercase">
                                        <p className="text-textSec">
                                            DIAGNOSIS
                                        </p>
                                        {(data && (
                                            <p className="text-base font-medium capitalize">
                                                {data.data.latest_diagnosis
                                                    .description || "--"}
                                            </p>
                                        )) || (
                                            <Skeleton
                                                containerTestId="basicinfo-loader"
                                                width="150px"
                                            />
                                        )}
                                    </div>

                                    <div className=" text-caption font-semibold  uppercase">
                                        <p className="text-textSec">
                                            DIAGNOSIS CODE
                                        </p>
                                        {(data && (
                                            <p className="text-base font-medium capitalize">
                                                {data.data.latest_diagnosis
                                                    .code || "--"}
                                            </p>
                                        )) || (
                                            <Skeleton
                                                containerTestId="basicinfo-loader"
                                                width="150px"
                                            />
                                        )}
                                    </div>
                                    {canViewEPrescribeFlow ? (
                                        <div className=" text-caption font-semibold  uppercase">
                                            <p className="text-textSec">
                                                E-Prescribe Status
                                            </p>
                                            {(data && (
                                                <div className="text-base font-medium capitalize flex items-center gap-x-2">
                                                    {getClientSyncStatusForDisplay()}
                                                    {/* !syncStatusError ? (
                                                        <ActionButton
                                                            variant="normal"
                                                            actionTitle="Update sync status"
                                                            className="[border-bottom:1px_solid_var(--color-text)] rounded-none"
                                                        >
                                                            <RefreshIcon />
                                                        </ActionButton>
                                                    ) : null */}
                                                </div>
                                            )) || (
                                                <Skeleton
                                                    containerTestId="basicinfo-loader"
                                                    width="150px"
                                                />
                                            )}
                                        </div>
                                    ) : null}
                                    <div className=" text-caption font-semibold  uppercase">
                                        <p className="text-textSec">
                                            LAST SESSION
                                        </p>
                                        {(data && (
                                            <p className="text-base font-medium capitalize">
                                                {formatDate(
                                                    data.data.last_session || ""
                                                ) || "--"}
                                            </p>
                                        )) || (
                                            <Skeleton
                                                containerTestId="basicinfo-loader"
                                                width="150px"
                                            />
                                        )}
                                    </div>

                                    <div className=" text-caption font-semibold  uppercase">
                                        <p className="text-textSec">
                                            insurance provider
                                        </p>
                                        {(data && (
                                            <p className="text-base font-medium uppercase">
                                                {data.data.insurance_provider
                                                    ? removeEnumUnderscore(
                                                          data.data
                                                              .insurance_provider
                                                      )
                                                    : "--"}
                                            </p>
                                        )) || (
                                            <Skeleton
                                                containerTestId="basicinfo-loader"
                                                width="150px"
                                            />
                                        )}
                                    </div>

                                    <div className="text-caption font-semibold ">
                                        <p className="text-textSec uppercase">
                                            FINANCIAL ASSISTANCE STATUS
                                        </p>
                                        {(data && (
                                            <span className="text-base capitalize">
                                                {data.data
                                                    .financial_assistance_status ||
                                                    "--"}
                                                {data.data
                                                    .financial_assistance_status &&
                                                    data.data
                                                        .financial_assistance_end_date &&
                                                    `(${
                                                        data.data
                                                            .financial_assistance_start_date
                                                            ? `Starts ${formatDate(
                                                                  data.data
                                                                      .financial_assistance_start_date,
                                                                  true
                                                              )}, `
                                                            : ""
                                                    }Ends ${formatDate(
                                                        data.data
                                                            .financial_assistance_end_date,
                                                        true
                                                    )})`}
                                            </span>
                                        )) || (
                                            <Skeleton
                                                containerTestId="basicinfo-loader"
                                                width="150px"
                                            />
                                        )}
                                    </div>

                                    <div className=" text-caption font-semibold  uppercase">
                                        <p className="text-textSec">Address</p>
                                        {(data && (
                                            <p className="text-base font-medium capitalize">
                                                {data.data.address}
                                            </p>
                                        )) || <Skeleton width="150px" />}
                                    </div>

                                    <div className=" text-caption font-semibold  uppercase">
                                        <p className="text-textSec">City</p>
                                        {(data && (
                                            <p className="text-base font-medium capitalize">
                                                {data.data.city || "--"}
                                            </p>
                                        )) || <Skeleton width="150px" />}
                                    </div>

                                    <div className=" text-caption font-semibold  uppercase">
                                        <p className="text-textSec">State</p>
                                        {(data && (
                                            <p className="text-base font-medium capitalize">
                                                {getStateInShortForm(
                                                    data.data.state
                                                )}
                                            </p>
                                        )) || <Skeleton width="150px" />}
                                    </div>

                                    <div className=" text-caption font-semibold  uppercase">
                                        <p className="text-textSec">Zipcode</p>
                                        {(data && (
                                            <p className="text-base font-medium capitalize">
                                                {data.data.zip_code || "--"}
                                            </p>
                                        )) || <Skeleton width="150px" />}
                                    </div>
                                    <div className=" text-caption font-semibold  uppercase">
                                        <p className="text-textSec">
                                            Consent to Sms
                                        </p>
                                        {(data && (
                                            <p className="text-base font-medium capitalize">
                                                {getConsentStatus(
                                                    data.data.consent_to_sms
                                                )}
                                            </p>
                                        )) || <Skeleton width="150px" />}
                                    </div>
                                    <div className="text-caption font-semibold uppercase">
                                        <p className="text-textSec">
                                            Portal Activation
                                        </p>
                                        {data ? (
                                            <p className="text-base font-medium capitalize">
                                                {getPortalActivationStatus(
                                                    data.data.last_login_date
                                                )}
                                            </p>
                                        ) : (
                                            <Skeleton width="150px" />
                                        )}
                                    </div>
                                    {getPortalActivationDate(
                                        data.data.first_login_date
                                    ) ? (
                                        <div className="text-caption font-semibold uppercase">
                                            <p className="text-textSec">
                                                Activation Date
                                            </p>
                                            <p className="text-base font-medium capitalize">
                                                {getPortalActivationDate(
                                                    data.data.first_login_date
                                                )}
                                            </p>
                                        </div>
                                    ) : null}
                                    {getLastLoginDate(
                                        data.data.last_login_date
                                    ) ? (
                                        <div className="text-caption font-semibold uppercase">
                                            <p className="text-textSec">
                                                Last Login
                                            </p>
                                            <p className="text-base font-medium capitalize">
                                                {getLastLoginDate(
                                                    data.data.last_login_date
                                                )}
                                            </p>
                                        </div>
                                    ) : null}
                                    {canSetClientBiller ? (
                                        <div className="text-caption font-semibold uppercase">
                                            <p className="text-textSec">
                                                Client can be charged by
                                            </p>
                                            <p className="text-base font-medium capitalize">
                                                {clientCanBeChargedBy}
                                            </p>
                                        </div>
                                    ) : null}
                                    <div className="text-caption font-semibold uppercase">
                                        <p className="text-textSec">
                                            Care Coordinator(s)
                                        </p>

                                        <p className="text-base font-medium capitalize">
                                            {coordinatorDisplay}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Card>
            )}
        </>
    );
}
